import React, { useRef } from "react";
import { Box, Grid } from "@mui/material";
import theme from "Theme/AppTheme";
import useHoverAction from "../../hooks/useHoverAction";
import ConditionHeader from "./ConditionHeader";
import Tag from "./Tag";
import AddButton from "../../components/AddButton";
import StatementContainer from "./StatementContainer";
import DeleteButton from "../../components/DeleteButton";
import {
    ConditionRuleType,
    FieldOptionsType,
    SelectedTargetFields,
    TargetFieldOptions,
} from "../../types";
import IfStatement from "./IFStatement";
import ErrorHandler from "../../components/ErrorHandler";

type ConditionRuleProps = {
    rule: ConditionRuleType;
    fieldOptions: FieldOptionsType[];
    conditionRules: ConditionRuleType[];
    getUpdatedTargetFields: () => {
        targetFieldOptions: TargetFieldOptions;
        selectedTargetFields: SelectedTargetFields;
    };
    setConditionRules: (conditionRules: ConditionRuleType[]) => void;
    updateTargetField: (value: string, targetFieldId: string) => void;
    updateIndicator: (indicatorId: string, key: string, value: string) => void;
    removeConditionIndicator: (indicatorId: string) => void;
    addConditionIndicator: () => void;
    removeConditionRule: () => void;
    addElseStatement: () => void;
};

const ConditionRule: React.FC<ConditionRuleProps> = ({
    rule,
    fieldOptions,
    conditionRules,
    getUpdatedTargetFields,
    setConditionRules,
    updateTargetField,
    updateIndicator,
    removeConditionIndicator,
    addConditionIndicator,
    removeConditionRule,
    addElseStatement,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { showAction, handlers } = useHoverAction(containerRef);

    const {
        id,
        targetFieldName,
        conditionIndicators,
        then,
        else: elseStatement,
        hasError,
        hasTargetFieldError,
    } = rule;

    return (
        <>
            <ErrorHandler
                hasError={hasError || hasTargetFieldError}
                errorMessage={`Please${
                    hasTargetFieldError ? " select Target Field and" : ""
                } fill in rule details to continue. Each statement should have a result and each if should have an else.`}
            />
            <Box
                sx={{
                    position: "relative",
                    border: `1px solid ${theme.palette.gray[200]}`,
                    borderRadius: 2,
                }}
                ref={containerRef}
                {...handlers}
            >
                <Grid
                    container
                    sx={{
                        gap: theme.spacing(2),
                    }}
                >
                    <ConditionHeader
                        ruleId={id}
                        showError={hasTargetFieldError}
                        updateTargetField={updateTargetField}
                        targetFieldName={targetFieldName}
                        getUpdatedTargetFields={getUpdatedTargetFields}
                    />
                    <Tag
                        text="IF"
                        sx={{
                            margin: theme.spacing(2, 1, 2, 2),
                        }}
                    />
                    <Grid
                        item
                        xs={10}
                        sx={{
                            padding: theme.spacing(2),
                            margin: theme.spacing(2, 0),
                            background: theme.palette.gray[100],
                            borderRadius: 2,
                        }}
                    >
                        {conditionIndicators.map(
                            (indicator, indicatorIndex) => {
                                const showSeparator =
                                    indicatorIndex !==
                                    conditionIndicators.length - 1;
                                return (
                                    <IfStatement
                                        hasError={hasError}
                                        key={indicator.id}
                                        indicator={indicator}
                                        removeIndicator={() => {
                                            removeConditionIndicator(
                                                indicator.id,
                                            );
                                        }}
                                        updateIndicator={(key, value) => {
                                            updateIndicator(
                                                indicator.id,
                                                key,
                                                value,
                                            );
                                        }}
                                        fieldOptions={fieldOptions}
                                        showSeparator={showSeparator}
                                    />
                                );
                            },
                        )}
                        <AddButton
                            onClick={() => {
                                addConditionIndicator();
                            }}
                        >
                            OR
                        </AddButton>
                        <Tag
                            text="THEN"
                            sx={{
                                marginTop: theme.spacing(2),
                            }}
                        />
                    </Grid>
                </Grid>

                <StatementContainer
                    statement={then}
                    ruleId={id}
                    conditionRules={conditionRules}
                    setConditionRules={setConditionRules}
                    fieldOptions={fieldOptions}
                />

                {elseStatement ? (
                    <Box sx={{ margin: theme.spacing(2) }}>
                        <StatementContainer
                            isElseTree
                            statement={elseStatement}
                            ruleId={id}
                            conditionRules={conditionRules}
                            setConditionRules={setConditionRules}
                            fieldOptions={fieldOptions}
                        />
                    </Box>
                ) : (
                    <AddButton
                        onClick={() => addElseStatement()}
                        sx={{ margin: theme.spacing(2) }}
                    >
                        ELSE
                    </AddButton>
                )}
                <DeleteButton
                    onDelete={() => removeConditionRule()}
                    showDelete={showAction}
                    sx={{
                        position: "absolute",
                        top: theme.spacing(1),
                        right: theme.spacing(1),
                    }}
                />
            </Box>
        </>
    );
};

export default ConditionRule;
